import { useEffect, useState } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Antarctica's numeric ID in world-atlas topojson
const ANTARCTICA_ID = '010';

interface City {
  name: string;
  coords: [number, number];
  textAnchor?: 'middle' | 'start' | 'end';
  dx?: number;
  dy?: number;
}

const CITIES: City[] = [
  { name: 'Houston',  coords: [-95.37, 29.76],  textAnchor: 'middle', dx:   0, dy: -14 },
  { name: 'London',   coords: [-0.13,  51.51],  textAnchor: 'middle', dx:   0, dy: -14 },
  { name: 'Lagos',    coords: [3.38,    6.52],  textAnchor: 'middle', dx:   0, dy: -14 },
  { name: 'Dubai',    coords: [55.27,  25.20],  textAnchor: 'end',    dx: -12, dy: -10 },
  { name: 'Muscat',   coords: [58.59,  23.61],  textAnchor: 'start',  dx:  12, dy:  16 },
  { name: 'Caracas',  coords: [-66.88, 10.48],  textAnchor: 'middle', dx:   0, dy: -14 },
];

// Canvas size — wide & tall to fill the section
const W = 1000;
const H =  480;

// Projection tuned to show Americas → Middle East, cut Antarctica
// Center longitude ~-15 (Atlantic), latitude ~25
const SCALE  = 195;
const CENTER_LNG = -12;
const CENTER_LAT =  22;

export default function WorldMap() {
  const [geoPaths, setGeoPaths] = useState<string[]>([]);
  const [pins, setPins] = useState<
    { name: string; x: number; y: number; textAnchor: string; dx: number; dy: number }[]
  >([]);

  useEffect(() => {
    fetch(GEO_URL)
      .then((r) => r.json())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((topo: any) => {
        const projection = geoNaturalEarth1()
          .scale(SCALE)
          .center([CENTER_LNG, CENTER_LAT])
          .translate([W / 2, H / 2]);

        const pathGen = geoPath(projection);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const countries = feature(topo, topo.objects.countries) as any;

        setGeoPaths(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          countries.features
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((f: any) => String(f.id) !== ANTARCTICA_ID)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((f: any) => pathGen(f) ?? ''),
        );

        setPins(
          CITIES.map(({ name, coords, textAnchor = 'middle', dx = 0, dy = -14 }) => {
            const [x, y] = projection(coords) ?? [0, 0];
            return { name, x, y, textAnchor, dx, dy };
          }),
        );
      })
      .catch(() => {});
  }, []);

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden"
      style={{ background: '#0b1f3a', aspectRatio: `${W}/${H}` }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" style={{ display: 'block' }}>
        {/* Ocean background */}
        <rect width={W} height={H} fill="#0b1f3a" />

        {/* Country fills */}
        {geoPaths.map((d, i) => (
          <path key={i} d={d} fill="#142d52" stroke="#1e406e" strokeWidth={0.5} />
        ))}

        {/* City pins */}
        {pins.map(({ name, x, y, textAnchor, dx, dy }) => (
          <g key={name} transform={`translate(${x},${y})`}>
            {/* Outer glow ring */}
            <circle r={14} fill="#d4571a" opacity={0.18} />
            {/* Mid ring */}
            <circle r={8}  fill="#d4571a" opacity={0.35} />
            {/* Dot */}
            <circle r={4}  fill="#d4571a" stroke="white" strokeWidth={1.5} />
            {/* Text — white halo for legibility */}
            <text
              textAnchor={textAnchor}
              x={dx}
              y={dy}
              fontSize={13}
              fill="none"
              stroke="#0b1f3a"
              strokeWidth={4}
              strokeLinejoin="round"
              fontWeight="700"
              fontFamily="'Inter', sans-serif"
              letterSpacing="0.04em"
            >
              {name}
            </text>
            {/* Label */}
            <text
              textAnchor={textAnchor}
              x={dx}
              y={dy}
              fontSize={13}
              fill="#f5a623"
              fontWeight="700"
              fontFamily="'Inter', sans-serif"
              letterSpacing="0.04em"
            >
              {name}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
